import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Slider } from '../components/ui/slider';
import { PageHero } from '../components/PageHero';
import { motion } from 'framer-motion';
import { Filter, CalendarIcon, Anchor, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { it, de, fr, enUS } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const locales = { it, de, fr, en: enUS };

// Format price with apostrophe as thousand separator (Swiss format)
const formatPrice = (price) => {
  if (!price) return '0';
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

export const BoatsPage = () => {
  const { t, language } = useLanguage();
  const [boats, setBoats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteImages, setSiteImages] = useState({});
  
  // Filters
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [lengthRange, setLengthRange] = useState([0, 30]);
  const [widthRange, setWidthRange] = useState([0, 10]);
  const [maxLength, setMaxLength] = useState(30);
  const [maxWidth, setMaxWidth] = useState(10);
  
  // Booking dialog
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_time: '10:00',
    visit_type: 'showroom',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [boatsRes, brandsRes, imagesRes] = await Promise.all([
          axios.get(`${API}/boats`),
          axios.get(`${API}/brands`),
          axios.get(`${API}/site-images`)
        ]);
        const boatsData = boatsRes.data;
        setBoats(boatsData);
        setBrands(brandsRes.data);
        setSiteImages(imagesRes.data);
        
        // Calculate max values for filters
        if (boatsData.length > 0) {
          const lengths = boatsData.map(b => b.length_m || 0);
          const widths = boatsData.map(b => b.width_m || 0);
          const maxL = Math.ceil(Math.max(...lengths));
          const maxW = Math.ceil(Math.max(...widths));
          setMaxLength(maxL > 0 ? maxL : 30);
          setMaxWidth(maxW > 0 ? maxW : 10);
          setLengthRange([0, maxL > 0 ? maxL : 30]);
          setWidthRange([0, maxW > 0 ? maxW : 10]);
        }
      } catch (error) {
        console.error('Error fetching boats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter boats
  const filterBoats = (boatsList) => {
    return boatsList.filter(boat => {
      if (selectedBrand !== 'all' && boat.brand !== selectedBrand) return false;
      if (boat.length_m && (boat.length_m < lengthRange[0] || boat.length_m > lengthRange[1])) return false;
      if (boat.width_m && (boat.width_m < widthRange[0] || boat.width_m > widthRange[1])) return false;
      return true;
    });
  };

  // Separate boats by condition
  const newBoats = filterBoats(boats.filter(b => b.condition === 'Nuovo'));
  const usedBoats = filterBoats(boats.filter(b => b.condition === 'Usato'));

  const getDescription = (boat) => {
    const key = `description_${language}`;
    return boat[key] || boat.description_it;
  };

  const handleBookVisit = (boat, e) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedBoat(boat);
    setBookingOpen(true);
  };

  const handleCardClick = (boat) => {
    if (boat.external_url) {
      window.open(boat.external_url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!bookingDate) {
      toast.error(t('visit.date'));
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/visit-bookings`, {
        ...bookingForm,
        boat_id: selectedBoat?.id,
        preferred_date: format(bookingDate, 'yyyy-MM-dd'),
        preferred_language: language
      });
      toast.success(t('visit.success'));
      setBookingOpen(false);
      setBookingForm({
        name: '',
        email: '',
        phone: '',
        preferred_time: '10:00',
        visit_type: 'showroom',
        notes: ''
      });
      setBookingDate(null);
    } catch (error) {
      toast.error(t('visit.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Boat Card Component
  const BoatCard = ({ boat, index }) => (
    <motion.div
      key={boat.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`boat-card-${boat.id}`}
      onClick={() => handleCardClick(boat)}
      className={`group bg-white border border-[#E5E5EA] hover:border-[#0B1221] transition-all duration-300 shadow-md hover:shadow-xl ${
        boat.external_url ? 'cursor-pointer' : ''
      }`}
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img
          src={boat.images[0]}
          alt={boat.name}
          className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 text-xs font-medium ${
            boat.condition === 'Nuovo' 
              ? 'bg-[#FF3B30] text-white' 
              : 'bg-white text-[#0B1221]'
          }`}>
            {boat.condition === 'Nuovo' ? t('boats.new') : t('boats.used')}
          </span>
        </div>
        {boat.external_url && (
          <div className="absolute top-4 left-4">
            <span className="bg-[#0B1221]/80 text-white px-2 py-1 text-xs flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />
              {language === 'it' ? 'Dettagli' : 'Details'}
            </span>
          </div>
        )}
      </div>
      
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-[#0B1221] text-xl">
            {boat.name}
          </h3>
        </div>
        
        <p className="text-[#FF3B30] text-sm font-medium mb-3">{boat.brand}</p>
        
        <p className="text-[#6E6E73] text-sm mb-4 line-clamp-2">
          {getDescription(boat)}
        </p>
        
        {/* Tutte le specifiche tecniche nel riquadro grigio */}
        <div className="bg-[#F5F5F7] p-4 rounded mb-4">
          <div className="text-xs text-[#6E6E73] uppercase tracking-wider mb-3 font-medium">
            {language === 'it' ? 'Specifiche Tecniche' : language === 'de' ? 'Technische Daten' : language === 'fr' ? 'Spécifications' : 'Technical Specs'}
          </div>
          
          {/* Dimensioni in lista verticale */}
          <div className="space-y-2 text-sm mb-3">
            <div className="flex justify-between items-center">
              <span className="text-[#6E6E73]">{language === 'it' ? 'Anno' : language === 'de' ? 'Jahr' : language === 'fr' ? 'Année' : 'Year'}</span>
              <span className="text-[#0B1221] font-semibold">{boat.year}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[#6E6E73]">{language === 'it' ? 'Lunghezza' : language === 'de' ? 'Länge' : language === 'fr' ? 'Longueur' : 'Length'}</span>
              <span className="text-[#0B1221] font-semibold">{Number(boat.length_m).toFixed(2)} m</span>
            </div>
            {boat.width_m > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-[#6E6E73]">{language === 'it' ? 'Larghezza' : language === 'de' ? 'Breite' : language === 'fr' ? 'Largeur' : 'Width'}</span>
                <span className="text-[#0B1221] font-semibold">{Number(boat.width_m).toFixed(2)} m</span>
              </div>
            )}
            {boat.capacity > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-[#6E6E73]">{language === 'it' ? 'Persone' : language === 'de' ? 'Personen' : language === 'fr' ? 'Personnes' : 'Capacity'}</span>
                <span className="text-[#0B1221] font-semibold">{boat.capacity}</span>
              </div>
            )}
          </div>
          
          {/* Separatore */}
          <div className="border-t border-[#E5E5EA] my-3"></div>
          
          {/* Motore */}
          <div className="text-xs text-[#6E6E73] uppercase tracking-wider mb-2">
            {language === 'it' ? 'Motorizzazione' : language === 'de' ? 'Motorisierung' : language === 'fr' ? 'Motorisation' : 'Engine'}
          </div>
          <div className="text-sm text-[#0B1221] font-semibold">
            {boat.num_engines > 1 ? `${boat.num_engines}x ` : ''}
            {boat.engine_brand || ''}{boat.engine_brand && boat.engine ? ' ' : ''}{boat.engine || ''}
            {boat.engine_power ? ` - ${boat.engine_power} CV` : ''}
          </div>
          {boat.engine_hours > 0 && (
            <div className="text-xs text-[#6E6E73] mt-1">
              {language === 'it' ? 'Ore motore' : language === 'de' ? 'Motorstunden' : language === 'fr' ? 'Heures' : 'Hours'}: {formatPrice(boat.engine_hours)}h
            </div>
          )}
        </div>
        
        <div className="border-t border-[#E5E5EA] pt-4">
          <div className="flex justify-between items-center">
            <span className="font-bold text-[#0B1221] text-xl">
              CHF {formatPrice(boat.price)}
            </span>
            <Button
              data-testid={`book-visit-${boat.id}`}
              onClick={(e) => handleBookVisit(boat, e)}
              variant="outline"
              className="rounded-full text-sm px-4 py-2 border-[#E5E5EA] hover:border-[#0B1221] hover:bg-[#0B1221] hover:text-white"
            >
              {t('boats.bookVisit')}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  // Section Component with different styling for new vs used
  const BoatSection = ({ title, subtitle, boats, sectionId, isNew }) => {
    if (boats.length === 0) return null;
    
    return (
      <section 
        data-testid={sectionId} 
        className={`py-16 ${isNew ? 'bg-gradient-to-b from-[#0B1221] to-[#1A2639]' : 'bg-[#F5F5F7]'}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className={`text-2xl sm:text-3xl font-bold mb-3 ${isNew ? 'text-white' : 'text-[#0B1221]'}`}>
              {title}
            </h2>
            {subtitle && (
              <p className={`text-lg ${isNew ? 'text-white/70' : 'text-[#6E6E73]'}`}>
                {subtitle}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {boats.map((boat, index) => (
              <BoatCard key={boat.id} boat={boat} index={index} />
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Helper to get background image URL
  const getHeroBg = () => siteImages.page_heroes?.boats || '';
  const hasHeroBg = !!getHeroBg();

  return (
    <div data-testid="boats-page" className="pt-20">
      {/* Hero with optional background - always dark */}
      <section className={`relative py-24 overflow-hidden ${hasHeroBg ? '' : 'bg-[#0B1221]'}`}>
        {/* Background Image/Video */}
        {hasHeroBg && (
          <>
            <div
              className="absolute inset-0 w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${getHeroBg()})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B1221]/70 via-[#0B1221]/60 to-[#0B1221]/70" />
          </>
        )}

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              {t('boats.title')}
            </h1>
            <p className="text-lg text-white/80">{t('boats.subtitle')}</p>
          </motion.div>

          {/* Filters */}
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-4">
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="flex items-center gap-2 text-[#6E6E73]">
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('boats.filter')}:</span>
                </div>
                
                <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                  <SelectTrigger data-testid="brand-filter" className="w-[180px] bg-[#F5F5F7] border-[#E5E5EA] text-[#0B1221]">
                    <SelectValue placeholder={t('boats.allBrands')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('boats.allBrands')}</SelectItem>
                    {brands.map((brand) => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 border-[#0B1221] text-[#0B1221] bg-transparent hover:bg-[#F5F5F7]"
              >
                {language === 'it' ? 'Filtri Avanzati' : language === 'de' ? 'Erweiterte Filter' : language === 'fr' ? 'Filtres Avancés' : 'Advanced Filters'}
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="border-t border-[#E5E5EA] pt-6 mt-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Length Filter */}
                  <div>
                    <label className="block text-sm font-medium text-[#0B1221] mb-3">
                      {language === 'it' ? 'Lunghezza' : language === 'de' ? 'Länge' : language === 'fr' ? 'Longueur' : 'Length'}: {lengthRange[0]}m - {lengthRange[1]}m
                    </label>
                    <Slider
                      value={lengthRange}
                      onValueChange={setLengthRange}
                      min={0}
                      max={maxLength}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  {/* Width Filter */}
                  <div>
                    <label className="block text-sm font-medium text-[#0B1221] mb-3">
                      {language === 'it' ? 'Larghezza' : language === 'de' ? 'Breite' : language === 'fr' ? 'Largeur' : 'Width'}: {widthRange[0]}m - {widthRange[1]}m
                    </label>
                    <Slider
                      value={widthRange}
                      onValueChange={setWidthRange}
                      min={0}
                      max={maxWidth}
                      step={0.5}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSelectedBrand('all');
                      setLengthRange([0, maxLength]);
                      setWidthRange([0, maxWidth]);
                    }}
                    className="text-sm text-[#6E6E73] hover:text-[#0B1221] hover:bg-[#F5F5F7]"
                  >
                    {language === 'it' ? 'Resetta Filtri' : language === 'de' ? 'Filter Zurücksetzen' : language === 'fr' ? 'Réinitialiser' : 'Reset Filters'}
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Boats Sections */}
      <div>
        {loading ? (
          <section className="py-16">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-[#F5F5F7] animate-pulse aspect-[4/5]" />
                ))}
              </div>
            </div>
          </section>
        ) : newBoats.length === 0 && usedBoats.length === 0 ? (
          <section className="py-16">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center py-20">
              <Anchor className="w-12 h-12 text-[#E5E5EA] mx-auto mb-4" />
              <p className="text-[#6E6E73]">{t('boats.noResults')}</p>
            </div>
          </section>
        ) : (
          <>
            {/* New Boats Section - Dark background */}
            <BoatSection 
              title={language === 'it' ? 'Imbarcazioni Nuove in Stock' : language === 'de' ? 'Neue Boote auf Lager' : language === 'fr' ? 'Bateaux Neufs en Stock' : 'New Boats in Stock'}
              subtitle={language === 'it' ? 'Pronte per la consegna immediata' : language === 'de' ? 'Sofort verfügbar' : language === 'fr' ? 'Disponibles immédiatement' : 'Ready for immediate delivery'}
              boats={newBoats}
              sectionId="new-boats-section"
              isNew={true}
            />

            {/* Used Boats Section - Light background */}
            <BoatSection 
              title={language === 'it' ? 'Imbarcazioni Usate' : language === 'de' ? 'Gebrauchte Boote' : language === 'fr' ? 'Bateaux d\'Occasion' : 'Used Boats'}
              subtitle={language === 'it' ? 'Selezionate e garantite' : language === 'de' ? 'Ausgewählt und garantiert' : language === 'fr' ? 'Sélectionnés et garantis' : 'Selected and guaranteed'}
              boats={usedBoats}
              sectionId="used-boats-section"
              isNew={false}
            />
          </>
        )}
      </div>

      {/* External Iframe Section */}
      {siteImages.boats_iframe && (
        <section className="py-16 lg:py-24 bg-[#F5F5F7]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            {/* Section Title */}
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1221] mb-4">
                {language === 'it' ? 'Tutte le nostre imbarcazioni in vendita' : 
                 language === 'de' ? 'Alle unsere Boote zum Verkauf' :
                 language === 'fr' ? 'Tous nos bateaux à vendre' :
                 'All our boats for sale'}
              </h2>
              <p className="text-[#6E6E73] text-lg max-w-2xl mx-auto">
                {language === 'it' ? 'Scopri tutti i dettagli e le specifiche tecniche delle nostre imbarcazioni' : 
                 language === 'de' ? 'Entdecken Sie alle Details und technischen Spezifikationen unserer Boote' :
                 language === 'fr' ? 'Découvrez tous les détails et spécifications techniques de nos bateaux' :
                 'Discover all details and technical specifications of our boats'}
              </p>
            </div>
            
            {/* Iframe Container */}
            <div 
              className="w-full overflow-hidden rounded-xl bg-white shadow-lg"
              style={{ minHeight: '800px' }}
              dangerouslySetInnerHTML={{ __html: siteImages.boats_iframe }}
            />
          </div>
        </section>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0B1221]">
              {t('visit.title')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBoat && (
            <div className="bg-[#F5F5F7] p-4 mb-4 flex gap-4">
              <img 
                src={selectedBoat.images[0]} 
                alt={selectedBoat.name}
                className="w-20 h-16 object-cover"
              />
              <div>
                <p className="font-semibold text-[#0B1221]">{selectedBoat.name}</p>
                <p className="text-sm text-[#6E6E73]">{selectedBoat.brand}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmitBooking} className="space-y-4">
            <div>
              <Input
                data-testid="booking-name"
                placeholder={t('contact.name')}
                value={bookingForm.name}
                onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                required
                className="bg-[#F5F5F7] border-none"
              />
            </div>
            
            <div>
              <Input
                data-testid="booking-email"
                type="email"
                placeholder={t('contact.email')}
                value={bookingForm.email}
                onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                required
                className="bg-[#F5F5F7] border-none"
              />
            </div>
            
            <div>
              <Input
                data-testid="booking-phone"
                type="tel"
                placeholder={t('contact.phone')}
                value={bookingForm.phone}
                onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                required
                className="bg-[#F5F5F7] border-none"
              />
            </div>

            <div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    data-testid="booking-date-trigger"
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-[#F5F5F7] border-none"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {bookingDate ? format(bookingDate, 'PPP', { locale: locales[language] || locales.en }) : t('visit.date')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={bookingDate}
                    onSelect={setBookingDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Select 
                value={bookingForm.preferred_time} 
                onValueChange={(value) => setBookingForm({...bookingForm, preferred_time: value})}
              >
                <SelectTrigger data-testid="booking-time" className="bg-[#F5F5F7] border-none">
                  <SelectValue placeholder={t('visit.time')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="09:00">09:00</SelectItem>
                  <SelectItem value="10:00">10:00</SelectItem>
                  <SelectItem value="11:00">11:00</SelectItem>
                  <SelectItem value="14:00">14:00</SelectItem>
                  <SelectItem value="15:00">15:00</SelectItem>
                  <SelectItem value="16:00">16:00</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select 
                value={bookingForm.visit_type} 
                onValueChange={(value) => setBookingForm({...bookingForm, visit_type: value})}
              >
                <SelectTrigger data-testid="booking-type" className="bg-[#F5F5F7] border-none">
                  <SelectValue placeholder={t('visit.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="showroom">{t('visit.showroom')}</SelectItem>
                  <SelectItem value="test_drive">{t('visit.testDrive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Textarea
                data-testid="booking-notes"
                placeholder={t('visit.notes')}
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                className="bg-[#F5F5F7] border-none min-h-[80px]"
              />
            </div>

            <Button
              data-testid="booking-submit"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full py-4"
            >
              {submitting ? '...' : t('visit.submit')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
